import { useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  Link,
  TextField,
  Typography,
  Container,
  Alert,
  Button,
  styled,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import useRefMounted from "../../../hooks/useRefMounted";
import { useTranslation } from "react-i18next";
import { fetchUtils } from "react-admin";
import { BASE_API_URL } from "config/configUrl";
import LogoCoinhouse from "../../../components/Logo/LogoCoinhouse";
import { brand } from "../../../themes/brandTokens";

const MainContent = styled(Box)(`
  min-height: 100vh;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`);

const Content = styled(Box)(`
  display: flex;
  flex: 1;
  width: 100%;
`);

const RecoverPasswordBasic = () => {
  const { t }: { t: any } = useTranslation();
  const isMountedRef = useRefMounted();

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (
    values: { email: string },
    { setSubmitting, setStatus, setErrors }: any
  ) => {
    try {
      const url = new URL(`${BASE_API_URL}/users/password/reset`);
      await fetchUtils.fetchJson(url.toString(), {
        method: "POST",
        body: JSON.stringify({ email: values.email }),
      });

      if (isMountedRef.current) {
        setSuccess(true);
        setError(null);
        setStatus({ success: true });
        setSubmitting(false);
      }
    } catch (err: any) {
      if (isMountedRef.current) {
        setError(err?.body?.message || t("Unexpected error"));
        setStatus({ success: false });
        setSubmitting(false);
        setErrors({ email: err?.body?.message });
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Coinhouse Solutions - Password Reset</title>
      </Helmet>
      <Content>
        <MainContent>
          <Container
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
            }}
            maxWidth="sm"
          >
            <Card
              sx={{
                p: { xs: 3, sm: 5 },
                my: 4,
                width: "100%",
                maxWidth: 480,
              }}
            >
              <Box textAlign="center">
                <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                  <LogoCoinhouse fillColor={brand.ink} />
                </Box>
                <Typography variant="h2" sx={{ mb: 1 }}>
                  {t("Reset your password")}
                </Typography>
                <Typography
                  variant="h4"
                  color="text.secondary"
                  fontWeight="normal"
                  sx={{ mb: 3 }}
                >
                  {t("Enter the email of your account and we will send you a reset link.")}
                </Typography>
              </Box>

              <Formik
                initialValues={{ email: "", submit: null }}
                validationSchema={Yup.object().shape({
                  email: Yup.string()
                    .email(
                      t("The email provided should be a valid email address")
                    )
                    .max(255)
                    .required(t("The email field is required")),
                })}
                onSubmit={handleSubmit}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  touched,
                  values,
                  isSubmitting,
                }) => (
                  <form noValidate onSubmit={handleSubmit}>
                    {error && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                      </Alert>
                    )}

                    {success && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        {t("If an account exists with this email, you will receive a link to reset your password. Check your inbox.")}
                      </Alert>
                    )}

                    <TextField
                      error={Boolean(touched.email && errors.email)}
                      fullWidth
                      helperText={touched.email && errors.email}
                      label={t("Email address")}
                      margin="normal"
                      name="email"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="email"
                      value={values.email}
                      variant="outlined"
                    />

                    <Button
                      sx={{ mt: 3 }}
                      color="primary"
                      disabled={isSubmitting}
                      type="submit"
                      fullWidth
                      size="large"
                      variant="contained"
                    >
                      {t("Send reset link")}
                    </Button>
                  </form>
                )}
              </Formik>

              <Box mt={3} textAlign="center">
                <Link component={RouterLink} to="/login" fontWeight={600}>
                  {t("Back to sign in")}
                </Link>
              </Box>
            </Card>
          </Container>
        </MainContent>
      </Content>
    </>
  );
};

export default RecoverPasswordBasic;
